'use client'

import React, { Suspense, memo, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { twMerge } from 'tailwind-merge'

import {
  useRecipientAnalyticsQueries,
  useSuspenseRecipientDashboard,
} from '@/hooks/usaspending'

import type { CompetitionBreakdown } from '@/types/usaspending'

import { QueryErrorBoundary } from '../../DataErrorBoundary'
import { Currency, Percentage } from '../../Formatters'
import { SectionCard, SectionSkeleton, useDetailFilter } from '../../shared'
import { BarListUnavailable } from '../../shared/ServiceUnavailable'
import { useRecipientDetail } from '../context'

const COMPETITION_COLORS = [
  'bg-emerald-500 dark:bg-emerald-600', // Full & Open
  'bg-amber-500 dark:bg-amber-600', // Competed Under SAP
  'bg-red-500 dark:bg-red-600', // Not Competed
  'bg-slate-400 dark:bg-slate-500', // Other
]

/**
 * RecipientDetail.Competition
 *
 * Shows competition method breakdown (Full & Open, Not Competed, etc.)
 * Visual indicator of competitive vs sole-source awards.
 */
export function CompetitionSection() {
  const { recipientId } = useRecipientDetail()

  return (
    <SectionCard
      id="competition"
      title="Competition Analysis"
      animationDelay={300}
    >
      <QueryErrorBoundary
        title="Failed to load competition data"
        serviceUnavailableFallback={
          <BarListUnavailable height={200} bars={4} />
        }
      >
        <Suspense fallback={<SectionSkeleton height={200} bars={4} />}>
          <CompetitionContent id={recipientId} />
        </Suspense>
      </QueryErrorBoundary>
    </SectionCard>
  )
}

function CompetitionContent({ id }: { id: string }) {
  const { state } = useDetailFilter()
  const fiscalYear = state.fiscalYear === 'all' ? undefined : state.fiscalYear
  const { data: dashboard } = useSuspenseRecipientDashboard(id, fiscalYear)

  if (dashboard.competition === null) {
    return <DeferredCompetition id={id} fiscalYear={fiscalYear} />
  }

  return <CompetitionList competition={dashboard.competition} />
}

function DeferredCompetition({
  id,
  fiscalYear,
}: {
  id: string
  fiscalYear?: number
}) {
  const queries = useRecipientAnalyticsQueries()
  const { data, isLoading } = useQuery(queries.competition(id, fiscalYear))

  if (isLoading) return <SectionSkeleton height={200} bars={4} />
  if (!data) return null

  return <CompetitionList competition={data} />
}

function CompetitionList({
  competition,
}: {
  competition: CompetitionBreakdown[]
}) {
  const total = competition.reduce((sum, d) => sum + d.amount, 0)

  return (
    <div className="space-y-4">
      {competition.slice(0, 5).map((item, index) => {
        const percentage = total > 0 ? (item.amount / total) * 100 : 0
        return (
          <CompetitionRow
            key={item.extentCompeted || 'unknown'}
            item={item}
            percentage={percentage}
            index={index}
          />
        )
      })}

      <style jsx>{`
        @keyframes barGrow {
          from {
            width: 0;
          }
        }
      `}</style>
    </div>
  )
}

/**
 * Memoized row component to prevent unnecessary re-renders (rerender-memo)
 */
const CompetitionRow = memo(function CompetitionRow({
  item,
  percentage,
  index,
}: {
  item: CompetitionBreakdown
  percentage: number
  index: number
}) {
  // Memoize style object to avoid recreation (rerender-memo-with-default-value)
  const barStyle = useMemo(
    () => ({
      width: `${percentage}%`,
      animation: `barGrow 400ms ease-out ${index * 75}ms backwards`,
    }),
    [percentage, index],
  )

  return (
    <div className="group w-full rounded-lg p-1 text-left transition-colors">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-sm font-medium text-primary-800 dark:text-primary-200">
          {item.description || 'Unknown'}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold tabular-nums text-primary-700 dark:text-primary-300">
            <Currency value={item.amount} compact />
          </span>
          <span className="text-xs tabular-nums text-primary-400 dark:text-primary-500">
            <Percentage value={percentage} />
          </span>
        </div>
      </div>
      <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-primary-100 dark:bg-primary-800">
        <div
          className={twMerge(
            'absolute inset-y-0 left-0 rounded-full transition-all duration-500',
            COMPETITION_COLORS[index] || COMPETITION_COLORS[3],
          )}
          style={barStyle}
        />
      </div>
    </div>
  )
})
